import { useCallback, useEffect, useState } from 'react';

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'qos-theme';

function readTheme(): Theme {
  // index.html resolves the theme before first paint; trust the DOM so React's
  // first render matches what the visitor is already looking at.
  return document.documentElement.dataset.theme === 'light' ? 'light' : 'dark';
}

/** Has the visitor picked a theme themselves, or are we still following the OS? */
function hasExplicitChoice(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) !== null;
  } catch {
    return false;
  }
}

/**
 * Theme state kept in sync with `<html data-theme>`.
 *
 * The choice is only written to storage when the visitor toggles, which keeps
 * "no preference yet" distinguishable from "deliberately chose dark" — so the
 * page can keep following the OS until they decide.
 */
export function useTheme() {
  const [theme, setTheme] = useState<Theme>(readTheme);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: light)');

    const onChange = (event: MediaQueryListEvent) => {
      if (!hasExplicitChoice()) setTheme(event.matches ? 'light' : 'dark');
    };

    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  const toggleTheme = useCallback(() => {
    const next: Theme = theme === 'dark' ? 'light' : 'dark';
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Private mode or blocked storage: the in-memory theme still applies.
    }
    setTheme(next);
  }, [theme]);

  return { theme, toggleTheme } as const;
}
